import { isValidPhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { validateEmail } from '../../utils/validation';

export interface ProfileDynamicFields {
  showSect: boolean;
  showCaste: boolean;
  showJamat: boolean;
  showManglik: boolean;
}

export interface ProfileRequiredFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  gender: string;
  dateOfBirth: string;
  motherTongueId: string;
  religionId: string;
  maritalStatusId: string;
  heightId: string;
  countryId: string;
  stateId: string;
  cityId: string;
  degreeId: string;
  employedInId: string;
  occupationId: string;
  income_currency_id: string;
  annualIncome: string;
  drinkingHabitId: string;
  smokingHabitId: string;
  dietId: string;
  sectId: string;
  casteId: string;
  jamatId: string;
  casteNoBar: boolean;
  manglikId: string;
  horoscopeNecessity: string;
}

export type ProfileValidationField = keyof ProfileRequiredFormData;

export const requiredFieldError = (label: string): string => `${label} is required`;

export const MIN_SIGNUP_AGE = 19;

const parseDateOnly = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);

  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }

  return date;
};

export const calculateAgeFromDateOfBirth = (dateOfBirth: string): number | null => {
  const birthDate = parseDateOnly(dateOfBirth);
  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
};

export const getMaxDateOfBirthForMinAge = (
  minAge: number = MIN_SIGNUP_AGE
): string => {
  const today = new Date();
  const maxBirthDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
  const year = maxBirthDate.getFullYear();
  const month = String(maxBirthDate.getMonth() + 1).padStart(2, '0');
  const day = String(maxBirthDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const validateDateOfBirthAge = (
  dateOfBirth: string,
  minAge: number = MIN_SIGNUP_AGE
): string | null => {
  const trimmed = dateOfBirth.trim();
  if (!trimmed) return null;

  const age = calculateAgeFromDateOfBirth(trimmed);
  if (age === null) {
    return 'Please enter a valid date of birth';
  }

  if (age < minAge) {
    return `User must be at least ${minAge} years old to sign up`;
  }

  return null;
};

export const PROFILE_FIELD_LABELS: Readonly<Record<ProfileValidationField, string>> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email Address',
  phoneNumber: 'Contact Number',
  gender: 'Gender',
  dateOfBirth: 'Date of Birth',
  motherTongueId: 'Mother Tongue',
  religionId: 'Religion',
  maritalStatusId: 'Marital Status',
  heightId: 'Height',
  countryId: 'Country',
  stateId: 'State',
  cityId: 'City',
  degreeId: 'Highest Degree',
  employedInId: 'Employed In',
  occupationId: 'Occupation',
  income_currency_id: 'Income Currency',
  annualIncome: 'Annual Income',
  drinkingHabitId: 'Do you Drink?',
  smokingHabitId: 'Do you Smoke?',
  dietId: 'Diet',
  horoscopeNecessity: 'Horoscope?',
  sectId: 'Sect',
  casteId: 'Caste',
  jamatId: 'Jamat',
  manglikId: 'Are you manglik?',
  casteNoBar: 'Caste no bar',
};

export const validateContactPhoneNumber = (phone?: string | null): string | null => {
  const trimmed = phone?.trim() ?? '';
  if (!trimmed) {
    return requiredFieldError(PROFILE_FIELD_LABELS.phoneNumber);
  }

  const normalized = trimmed.replace(/\s+/g, '');
  if (isValidPhoneNumber(normalized)) {
    return null;
  }

  const parsed = parsePhoneNumberFromString(normalized);
  if (parsed?.isValid()) {
    return null;
  }

  return 'Please enter a valid phone number';
};

export const PROFILE_REQUIRED_FIELD_CHECKS: ReadonlyArray<{
  field: ProfileValidationField;
  label: string;
}> = [
  { field: 'firstName', label: PROFILE_FIELD_LABELS.firstName },
  { field: 'lastName', label: PROFILE_FIELD_LABELS.lastName },
  { field: 'phoneNumber', label: PROFILE_FIELD_LABELS.phoneNumber },
  { field: 'gender', label: PROFILE_FIELD_LABELS.gender },
  { field: 'dateOfBirth', label: PROFILE_FIELD_LABELS.dateOfBirth },
  { field: 'motherTongueId', label: PROFILE_FIELD_LABELS.motherTongueId },
  { field: 'religionId', label: PROFILE_FIELD_LABELS.religionId },
  { field: 'maritalStatusId', label: PROFILE_FIELD_LABELS.maritalStatusId },
  { field: 'heightId', label: PROFILE_FIELD_LABELS.heightId },
  { field: 'countryId', label: PROFILE_FIELD_LABELS.countryId },
  { field: 'stateId', label: PROFILE_FIELD_LABELS.stateId },
  { field: 'cityId', label: PROFILE_FIELD_LABELS.cityId },
  { field: 'degreeId', label: PROFILE_FIELD_LABELS.degreeId },
  { field: 'employedInId', label: PROFILE_FIELD_LABELS.employedInId },
  { field: 'occupationId', label: PROFILE_FIELD_LABELS.occupationId },
  { field: 'income_currency_id', label: PROFILE_FIELD_LABELS.income_currency_id },
  { field: 'annualIncome', label: PROFILE_FIELD_LABELS.annualIncome },
  { field: 'drinkingHabitId', label: PROFILE_FIELD_LABELS.drinkingHabitId },
  { field: 'smokingHabitId', label: PROFILE_FIELD_LABELS.smokingHabitId },
  { field: 'dietId', label: PROFILE_FIELD_LABELS.dietId },
  { field: 'horoscopeNecessity', label: PROFILE_FIELD_LABELS.horoscopeNecessity },
];

export type ProfileValidationErrors = Partial<Record<ProfileValidationField, string>>;

export const getProfileFormValidationErrors = (
  formData: ProfileRequiredFormData,
  dynamicFields: ProfileDynamicFields,
  phoneNumberOverride?: string
): ProfileValidationErrors => {
  const errors: ProfileValidationErrors = {};

  const emailResult = validateEmail(formData.email.trim());
  if (!emailResult.valid && emailResult.error) {
    errors.email = emailResult.error;
  }

  for (const { field, label } of PROFILE_REQUIRED_FIELD_CHECKS) {
    if (field === 'phoneNumber') continue;

    const value = formData[field];
    if (typeof value === 'string' && !value.trim()) {
      errors[field] = requiredFieldError(label);
    }
  }

  if (formData.dateOfBirth.trim()) {
    const dateOfBirthError = validateDateOfBirthAge(formData.dateOfBirth);
    if (dateOfBirthError) {
      errors.dateOfBirth = dateOfBirthError;
    }
  }

  if (dynamicFields.showSect && !formData.sectId.trim()) {
    errors.sectId = requiredFieldError(PROFILE_FIELD_LABELS.sectId);
  }

  if (
    (dynamicFields.showCaste || (dynamicFields.showSect && formData.sectId)) &&
    !formData.casteNoBar &&
    !formData.casteId.trim()
  ) {
    errors.casteId = requiredFieldError(PROFILE_FIELD_LABELS.casteId);
  }

  if (dynamicFields.showJamat && !formData.jamatId.trim()) {
    errors.jamatId = requiredFieldError(PROFILE_FIELD_LABELS.jamatId);
  }

  if (dynamicFields.showManglik && !formData.manglikId.trim()) {
    errors.manglikId = requiredFieldError(PROFILE_FIELD_LABELS.manglikId);
  }

  const phone = phoneNumberOverride ?? formData.phoneNumber;
  const phoneError = validateContactPhoneNumber(phone);
  if (phoneError) {
    errors.phoneNumber = phoneError;
  }

  return errors;
};

export const getProfileFormValidationError = (
  formData: ProfileRequiredFormData,
  dynamicFields: ProfileDynamicFields,
  phoneNumberOverride?: string
): string | null => {
  const errors = getProfileFormValidationErrors(formData, dynamicFields, phoneNumberOverride);
  const firstError = Object.values(errors)[0];
  return firstError ?? null;
};

export const isProfileFormComplete = (
  formData: ProfileRequiredFormData,
  dynamicFields: ProfileDynamicFields,
  phoneNumberOverride?: string
): boolean =>
  getProfileFormValidationError(formData, dynamicFields, phoneNumberOverride) === null;
